import type { UndoEntryItem } from '@/states/undo-stack.state';
import {
  RecomputeDealItemsPricingDocument,
  type PricingRecomputeTarget,
  type RecomputeDealItemsPricingMutation,
} from '@/lib/graphql/generated/graphql';
import { useUndoDealItems } from '@/lib/hooks/useUndoDealItems';
import { dealItemsRefetchState } from '@/states/products.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';

type RecomputeResult = Extract<
  RecomputeDealItemsPricingMutation['recomputeDealItemsPricing'],
  { __typename: 'RecomputeDealItemsPricingSuccess' }
>['results'][number];

export const undoItemsOfRecompute = (
  results: RecomputeResult[]
): UndoEntryItem[] =>
  results.flatMap((item) =>
    item.previousHistoryId &&
    item.currentHistoryId &&
    item.previousHistoryId !== item.currentHistoryId
      ? [
          {
            dealItemId: item.id,
            previousHistoryId: item.previousHistoryId,
            currentHistoryId: item.currentHistoryId,
          },
        ]
      : []
  );

export function useRecomputeDealItemsPricing(dealId: string) {
  const [mutate, { loading }] = useMutation(RecomputeDealItemsPricingDocument);
  const { pushUndoEntry } = useUndoDealItems(dealId);
  const refetch = useAtomValue(dealItemsRefetchState);

  const recompute = async (
    itemIds: string[],
    target: PricingRecomputeTarget,
    { recordUndo = true }: { recordUndo?: boolean } = {}
  ) => {
    const result = await mutate({ variables: { dealId, itemIds, target } });
    const response = result.data?.recomputeDealItemsPricing;
    if (response?.__typename !== 'RecomputeDealItemsPricingSuccess') {
      throw new Error(
        response?.__typename === 'DomainException'
          ? response.code
          : 'Missing pricing recalculation result'
      );
    }
    if (recordUndo) pushUndoEntry(undoItemsOfRecompute(response.results));
    // Subscriptions deliver the subsequent validation verdict. A refresh alone is not resolution.
    let refreshed = false;
    try {
      refreshed = (await refetch?.fn()) ?? false;
    } catch {
      refreshed = false;
    }
    return { results: response.results, refreshed };
  };

  return { recompute, loading };
}
